import { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Empty, Modal, Switch, Table } from 'antd';
import type { ButtonProps } from 'antd';

import InfiniteScroller from 'components/molecules/InfiniteScroller';

import type { ImportEventSourceCategory } from '../../../config/importEventFromPlanConfig';
import {
    TPO_IMPORT_EVENTS_CANCEL_BTN,
    TPO_IMPORT_EVENTS_CONFIRM_BTN,
    TPO_IMPORT_EVENTS_EMPTY,
    TPO_IMPORT_EVENTS_MODAL,
    TPO_IMPORT_EVENTS_PLAN_TYPE_SWITCH,
    TPO_IMPORT_EVENTS_TABLE,
    tpoTableRowTestId,
} from '../../../config/testIds';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import { TPOGEN_IMPORT_EVENT_STARTED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../../utils/trackTpoGenEvent';
import useImportEventsMutation from '../hooks/mutations/useImportEventsMutation';
import useImportableEvents from '../hooks/queries/useImportableEvents';
import type { ImportableEventRow } from '../hooks/queries/useImportableEvents';
import useImportablePlans from '../hooks/queries/useImportablePlans';
import { useImportEventColumns } from '../hooks/ui/useImportEventColumns';
import { useImportEventsForm } from '../hooks/ui/useImportEventsForm';
import ImportEventSourceToolbar from './ImportEventSourceToolbar';
import ImportEventsFooter from './ImportEventsFooter';

const TABLE_HEIGHT = 378;
/** The scrollport InfiniteScroller renders; the sticky header must anchor to it, not the window. */
const SCROLLER_SELECTOR = '.infinite-scroll-component';
const OK_BUTTON_STYLE = { borderRadius: 6 };
const CANCEL_BUTTON_STYLE = { borderColor: '#44338a', color: '#44338a', borderRadius: 6 };

const NO_PLAN_SELECTED_LOCALE = {
    emptyText: (
        <div data-testid={TPO_IMPORT_EVENTS_EMPTY}>
            <Empty description="Select a plan to view events" />
        </div>
    ),
};

interface Props {
    open: boolean;
    onClose: () => void;
    targetPlanId: string;
}

const ImportEventFromPlanModal: FC<Props> = ({ open, onClose, targetPlanId }) => {
    const { importEventFromPlanConfig, fieldValueLabelMaps } = useTpoGenConfig();
    const {
        modalTitle,
        modalWidth = 1200,
        defaultPlanType,
        sourceLabels,
        tableColumns: columnConfigs,
    } = importEventFromPlanConfig;

    const scrollerWrapRef = useRef<HTMLDivElement>(null);

    const {
        isTpmPlan,
        selectedSource,
        sourcePlanId,
        planSearch,
        eventSearch,
        eventSearchKeyword,
        selectedPromoIds,
        setPlanSearch,
        setEventSearch,
        setSelectedPromoIds,
        changePlanType,
        changeSource,
        changePlan,
        submitEventSearch,
        reset,
    } = useImportEventsForm(defaultPlanType);

    const {
        plansByCategory,
        isPending: plansPending,
        isFetchingNextPage: plansFetchingNextPage,
        hasNextPage: plansHasNextPage,
        fetchNextPage: plansFetchNextPage,
    } = useImportablePlans({ planId: targetPlanId, searchString: planSearch, isTPMPlan: isTpmPlan });

    const {
        events,
        totalCount: eventsTotalCount,
        isPending: eventsPending,
        isFetching: eventsFetching,
        isFetchingNextPage: eventsFetchingNextPage,
        hasNextPage: eventsHasNextPage,
        fetchNextPage: eventsFetchNextPage,
    } = useImportableEvents({ sourcePlanId, targetPlanId, searchString: eventSearchKeyword });

    const { mutate: importEvents, isPending: importPending } = useImportEventsMutation({
        onSuccess: () => onClose(),
    });

    const tableColumns = useImportEventColumns(columnConfigs, fieldValueLabelMaps);

    const availableCategories = useMemo<ImportEventSourceCategory[]>(() => {
        const cats: ImportEventSourceCategory[] = ['self'];
        if (plansByCategory.parent.length > 0) cats.push('parent');
        if (plansByCategory.child.length > 0) cats.push('child');
        return cats;
    }, [plansByCategory]);

    const planOptions = useMemo(
        () => plansByCategory[selectedSource].map((p) => ({ label: p.plan_name, value: p.plan_id })),
        [plansByCategory, selectedSource],
    );

    const handleImport = useCallback(() => {
        if (!sourcePlanId || selectedPromoIds.length === 0) return;
        trackTpoGenEvent(TPOGEN_IMPORT_EVENT_STARTED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
            source_plan_id: sourcePlanId,
            target_plan_id: targetPlanId,
            // `date_shift_mode` omitted: no date-shift feature exists in the import
            // flow today (imported events/promos keep their original dates verbatim).
        });
        importEvents({ sourcePlanId, targetPlanId, promoIds: selectedPromoIds });
    }, [sourcePlanId, targetPlanId, selectedPromoIds, importEvents]);

    const handleRowSelectionChange = useCallback(
        (keys: React.Key[]) => setSelectedPromoIds(keys as string[]),
        [setSelectedPromoIds],
    );

    // Scoped to this modal: the calendar behind it renders its own scroller earlier in the DOM.
    const getStickyContainer = useCallback(
        (): HTMLElement | Window => scrollerWrapRef.current?.querySelector<HTMLElement>(SCROLLER_SELECTOR) ?? window,
        [],
    );

    useEffect(() => {
        if (!open) reset();
    }, [open, reset]);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={handleImport}
            okText="Import"
            okButtonProps={
                {
                    disabled: selectedPromoIds.length === 0,
                    loading: importPending,
                    style: OK_BUTTON_STYLE,
                    'data-testid': TPO_IMPORT_EVENTS_CONFIRM_BTN,
                } as ButtonProps
            }
            cancelButtonProps={
                {
                    disabled: importPending,
                    style: CANCEL_BUTTON_STYLE,
                    'data-testid': TPO_IMPORT_EVENTS_CANCEL_BTN,
                } as ButtonProps
            }
            width={modalWidth}
            title={
                <div className="flex items-center gap-3">
                    <span>{modalTitle}</span>
                    <div className="flex items-center gap-1 font-normal">
                        <span className="text-[10px] text-[#807e86]">TPM</span>
                        <Switch
                            checked={!isTpmPlan}
                            onChange={(checked) => changePlanType(!checked)}
                            data-testid={TPO_IMPORT_EVENTS_PLAN_TYPE_SWITCH}
                            data-state-plan-type={isTpmPlan ? 'tpm' : 'tpo'}
                        />
                        <span className="text-[10px] text-[#807e86]">TPO</span>
                    </div>
                </div>
            }
            destroyOnClose
            maskClosable={!importPending}
            closable={!importPending}
        >
            <div className="flex flex-col gap-2" data-testid={TPO_IMPORT_EVENTS_MODAL}>
                <ImportEventSourceToolbar
                    sourceLabels={sourceLabels}
                    availableCategories={availableCategories}
                    selectedSource={selectedSource}
                    onSourceChange={changeSource}
                    planOptions={planOptions}
                    sourcePlanId={sourcePlanId}
                    onPlanChange={changePlan}
                    plansLoading={plansPending || plansFetchingNextPage}
                    plansHasMore={plansHasNextPage}
                    onPlansScrollEnd={plansFetchNextPage}
                    onPlanSearch={setPlanSearch}
                    eventSearch={eventSearch}
                    onEventSearchChange={setEventSearch}
                    onEventSearchSubmit={submitEventSearch}
                />

                <div className="pb-3" ref={scrollerWrapRef}>
                    <InfiniteScroller
                        data={events}
                        onNext={eventsFetchNextPage}
                        hasMore={eventsHasNextPage}
                        loading={eventsFetchingNextPage}
                        height={TABLE_HEIGHT}
                    >
                        <Table<ImportableEventRow>
                            columns={tableColumns}
                            dataSource={events}
                            rowKey="promo_id"
                            pagination={false}
                            loading={eventsPending || eventsFetching}
                            bordered
                            sticky={{ getContainer: getStickyContainer }}
                            size="middle"
                            locale={sourcePlanId ? undefined : NO_PLAN_SELECTED_LOCALE}
                            data-testid={TPO_IMPORT_EVENTS_TABLE}
                            onRow={(record) =>
                                ({
                                    'data-testid': tpoTableRowTestId('import_events', record.promo_id),
                                } as React.HTMLAttributes<HTMLTableRowElement>)
                            }
                            rowSelection={{
                                type: 'checkbox',
                                selectedRowKeys: selectedPromoIds,
                                preserveSelectedRowKeys: true,
                                onChange: handleRowSelectionChange,
                            }}
                        />
                    </InfiniteScroller>
                    {sourcePlanId ? (
                        <ImportEventsFooter
                            loadedCount={events.length}
                            totalCount={eventsTotalCount}
                            selectedCount={selectedPromoIds.length}
                        />
                    ) : null}
                </div>
            </div>
        </Modal>
    );
};

ImportEventFromPlanModal.displayName = 'ImportEventFromPlanModal';
export default ImportEventFromPlanModal;
